import { redact } from './redact';

/**
 * Log estruturado em JSON (§9.10, `system-design.md` §8.5).
 *
 * O BetterStack ingere JSON. Uma linha malformada é uma linha perdida — e logs perdidos são
 * descobertos justamente durante um incidente, que é quando menos se pode perdê-los.
 *
 * Usa a **mesma redação do Sentry**: um log que imprime `email` em JSON é o mesmo vazamento por
 * outra porta.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const ORDEM: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };

export interface LoggerOptions {
  escrever?: (linha: string) => void;
  nivelMinimo?: LogLevel;
  agora?: () => Date;
}

export interface Logger {
  debug(mensagem: string, contexto?: Record<string, unknown>): void;
  info(mensagem: string, contexto?: Record<string, unknown>): void;
  warn(mensagem: string, contexto?: Record<string, unknown>): void;
  error(mensagem: string, contexto?: Record<string, unknown>): void;
}

/**
 * Serializa tolerando ciclos.
 *
 * `redact` protege contra laço infinito, mas não remove a referência circular — e `JSON.stringify`
 * lança nela. Um logger que rebenta ao registar um objeto com ciclo falha exatamente onde é preciso.
 */
function serializar(valor: unknown): string {
  const vistos = new WeakSet<object>();

  return JSON.stringify(valor, (_chave, v) => {
    if (v && typeof v === 'object') {
      if (vistos.has(v)) return '[circular]';
      vistos.add(v);
    }
    return v;
  });
}

export function createLogger({
  escrever = (linha) => console.log(linha),
  nivelMinimo = 'info',
  agora = () => new Date(),
}: LoggerOptions = {}): Logger {
  const emitir = (level: LogLevel, message: string, context?: Record<string, unknown>) => {
    if (ORDEM[level] < ORDEM[nivelMinimo]) return;

    // Redige antes de serializar; o contexto é mutado no lugar, como no `beforeSend`.
    if (context) redact(context);

    escrever(
      serializar({
        level,
        message,
        timestamp: agora().toISOString(),
        ...(context ? { context } : {}),
      }),
    );
  };

  return {
    debug: (m, c) => emitir('debug', m, c),
    info: (m, c) => emitir('info', m, c),
    warn: (m, c) => emitir('warn', m, c),
    error: (m, c) => emitir('error', m, c),
  };
}
